import { type ReactNode } from 'react'

import Markdown from 'markdown-to-jsx'
import { Link } from 'react-router-dom'

import { ConversationMarkdownInlineStyled } from './styles/ConversationMarkdownInlineStyled'
import { ConversationMarkdownStyled } from './styles/ConversationMarkdownStyled'
import { ConversationMarkdownTableScrollStyled } from './styles/ConversationMarkdownTableScrollStyled'

export interface IConversationMarkdownProps {
  content: string
  /**
   * Renders without block elements, for content that sits inside a line of
   * text such as a table cell or a delivery label.
   */
  isInline?: boolean
}

interface IMarkdownLinkProps {
  href?: string
  children?: ReactNode
}

interface IMarkdownImageProps {
  alt?: string
}

interface IMarkdownTableProps {
  children?: ReactNode
}

// An href the library rejected as unsafe arrives with no href at all, so the
// label stays as plain text. Only the in-app vs external choice is made here.
const MarkdownLink = ({ href, children }: IMarkdownLinkProps) => {
  if (href === undefined) return <>{children}</>

  if (href.startsWith('/')) return <Link to={href}>{children}</Link>

  return href.startsWith('http') ? (
    <a href={href} target='_blank' rel='noopener noreferrer'>
      {children}
    </a>
  ) : (
    <a href={href}>{children}</a>
  )
}

// Replies summarise text we do not control, so a remote image is never
// fetched. The alt text stands in for it.
const MarkdownImage = ({ alt }: IMarkdownImageProps) => <>{alt}</>

const MarkdownTable = ({ children }: IMarkdownTableProps) => (
  <ConversationMarkdownTableScrollStyled>
    <table>{children}</table>
  </ConversationMarkdownTableScrollStyled>
)

// Agent replies summarise text we do not control - vendor descriptions,
// catalog data - so raw HTML is never turned into elements. Injected markup
// shows as text instead of becoming a form, an overlay or a remote stylesheet.
const MARKDOWN_OPTIONS = {
  disableParsingRawHTML: true,
  overrides: { a: MarkdownLink, img: MarkdownImage, table: MarkdownTable },
}

const INLINE_MARKDOWN_OPTIONS = { ...MARKDOWN_OPTIONS, forceInline: true }

export const ConversationMarkdown = ({
  content,
  isInline = false,
}: IConversationMarkdownProps) =>
  isInline ? (
    <ConversationMarkdownInlineStyled>
      <Markdown options={INLINE_MARKDOWN_OPTIONS}>{content}</Markdown>
    </ConversationMarkdownInlineStyled>
  ) : (
    <ConversationMarkdownStyled>
      <Markdown options={MARKDOWN_OPTIONS}>{content}</Markdown>
    </ConversationMarkdownStyled>
  )
